import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import RuleCatalog from "@dashboard/policies/rules/RuleCatalog";
import { MdmPolicyCommand } from "@devices/types";
import {
    MinimalTable,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { ColumnDef, getCoreRowModel, Table, useReactTable } from "@tanstack/react-table";

import PolicyRuleItem, { type PolicyRulesUpdate } from "../PolicyRuleItem/PolicyRuleItem";

import { DraggablePolicyRulesBody } from "./DraggablePolicyRulesBody.lazy";
import { getRowKeys } from "./policyRuleRowKeys";

interface PolicyRulesTableProps {
    content: MdmPolicyCommand[];
    onUpdate?: PolicyRulesUpdate;
    readOnly?: boolean;
    loading?: boolean;
}

interface PolicyRulesTableBodyProps {
    content: MdmPolicyCommand[];
    onUpdate?: PolicyRulesUpdate;
    readOnly: boolean;
    loading: boolean;
    table: Table<MdmPolicyCommand>;
    noDataMessage: string;
}

const columns: ColumnDef<MdmPolicyCommand>[] = [{ id: "drag" }, { id: "warning" }, { id: "command" }, { id: "actions" }];

const PolicyRulesTableBody = ({ content, onUpdate, readOnly, loading, table, noDataMessage }: PolicyRulesTableBodyProps) => {
    const rowKeys = getRowKeys(content);
    const duplicatedIndexes = useMemo(() => RuleCatalog.getDuplicatedIndexes(content), [content]);

    if (readOnly) {
        return (
            <TableBody table={table} noDataMessage={noDataMessage}>
                {content.map((c, index) => (
                    <PolicyRuleItem
                        index={index}
                        content={content}
                        policyCommand={c}
                        key={rowKeys[index]}
                        duplicated={duplicatedIndexes.has(index)}
                        readOnly
                    />
                ))}
            </TableBody>
        );
    }

    if (!content.length) {
        return <TableBody table={table} noDataMessage={noDataMessage} children={null} />;
    }

    return <DraggablePolicyRulesBody content={content} onUpdate={onUpdate} loading={loading} />;
};

const PolicyRulesTable = ({ content, onUpdate, readOnly = false, loading = false }: PolicyRulesTableProps) => {
    const { t } = useTranslation(["sidepanels", "pages"]);

    const table = useReactTable<MdmPolicyCommand>({ data: content, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <MinimalTableContainer>
            <MinimalTable stickyHeader>
                <MinimalTableHeader>
                    <MinimalTableRow>
                        <MinimalTableHeaderCell width={50} />
                        <MinimalTableHeaderCell width={20} />
                        <MinimalTableHeaderCell align="left">{t("pages:policiesPage.table.header.rule")}</MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="right" />
                    </MinimalTableRow>
                </MinimalTableHeader>
                <PolicyRulesTableBody
                    content={content}
                    onUpdate={onUpdate}
                    readOnly={readOnly}
                    loading={loading}
                    table={table}
                    noDataMessage={t("pages:policiesPage.section.rules.empty")}
                />
            </MinimalTable>
        </MinimalTableContainer>
    );
};

export default PolicyRulesTable;
